import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, List, Skeleton, Empty, Avatar } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts } from '../Redux/slice/productSlice';
import { useNavigate } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';

const backendBaseURL = 'https://smfteapi.salesmate.app';

const SearchModal = ({ visible, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { products = [], loading } = useSelector((state) => state.products);

  // Fetch products when modal opens
  useEffect(() => {
    if (visible && products.length === 0) {
      dispatch(fetchProducts());
    }

    // Cleanup: fetch fresh products when modal closes/unmounts
    return () => {
      dispatch(fetchProducts());
    };
  }, [visible, dispatch, products.length]);

  // Clear input and search when modal closes
  useEffect(() => {
    if (!visible) {
      setInputValue('');
      setSearchQuery('');
    } else {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [visible]);

  // Debounce logic setup
  useEffect(() => {
    debounceRef.current = debounce((value) => setSearchQuery(value), 300);
    return () => debounceRef.current.cancel();
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debounceRef.current(value);
  };

  const navigateToProduct = (productID) => {
    navigate(`/product/${productID}`);
    onClose();
  };

  const formatPrice = (price) => `₵${price?.toLocaleString?.() || 'N/A'}`;

  const highlightText = (text = '') => {
    if (!searchQuery) return text;
    const regex = new RegExp(`(${searchQuery})`, 'gi');
    return text.replace(regex, '<span style="background-color: yellow;">$1</span>');
  };

  const filteredProducts = searchQuery
    ? products.filter((product) =>
        product.productName?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : products;

  return (
    <Modal
      title="Search for Products"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleSearchChange}
        placeholder="Search for a product"
        style={{ marginBottom: '20px', borderRadius: '20px' }}
        prefix={<SearchOutlined />}
        allowClear
      />

{loading ? (
  <List
    dataSource={Array.from({ length: 5 }, (_, i) => ({ key: i }))}
    renderItem={(item) => (
      <List.Item key={item.key}>
        <Skeleton avatar title={{ width: '60%' }} active />
      </List.Item>
    )}
  />
) :

 inputValue.trim() === '' ? (
        <Empty description="Start typing to search for products" />
      ) : filteredProducts.length === 0 ? (
        <Empty description="No products found" />
      ) : (
        <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '10px' }}>
          <List
            itemLayout="horizontal"
            dataSource={filteredProducts}
            renderItem={(item) => {
              const imagePath = item.productImage?.split('\\').pop();
              const imageURL = `${backendBaseURL}/Media/Products_Images/${imagePath}`;

              return (
                <List.Item
                  key={item.productID}
                  onClick={() => navigateToProduct(item.productID)}
                  className="hover:bg-gray-100 transition-all duration-200 cursor-pointer mb-2 rounded px-2"
                >
                  <List.Item.Meta
                    avatar={<Avatar src={imageURL} />}
                    title={
                      <span
                        dangerouslySetInnerHTML={{
                          __html: highlightText(item.productName || ''),
                        }}
                        style={{ color: '#006838', fontWeight: 'bold' }}
                      />
                    }
                    description={
                      <span style={{ color: '#D72638' }}>
                        Price: {formatPrice(item.price)}
                      </span>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </div>
      )}
    </Modal>
  );
};

export default SearchModal;
